import type { AnalysisStatisticsData } from '@/features/analysisPageStatistics/types';

/**
 * Score de niche /100 — synthèse client-side des métriques clés.
 *
 * Pondération :
 * - 50 % : % de vente (30 %+ = plafond — au-delà, la niche est excellente)
 * - 30 % : vélocité (20 ventes/jour = plafond)
 * - 20 % : temps de vente (0 j = max, 14 j+ = 0)
 *
 * Les seuils viennent des repères mentaux des utilisateurs (10/15/20 % de
 * sell-through) extrapolés — à recalibrer avec la data réelle v2.
 */
export const computeNicheScore = (stats: AnalysisStatisticsData | undefined): number | null => {
  if (!stats) return null;
  const { salePercentage, salesPerDay, averageSaleTimeSec } = stats;
  if (salePercentage == null && salesPerDay == null && averageSaleTimeSec == null) return null;

  const pctScore = Math.min((salePercentage ?? 0) / 30, 1);
  const velocityScore = Math.min((salesPerDay ?? 0) / 20, 1);
  const saleTimeDays = (averageSaleTimeSec ?? 14 * 24 * 3600) / (24 * 3600);
  const timeScore = 1 - Math.min(saleTimeDays / 14, 1);

  return Math.round((0.5 * pctScore + 0.3 * velocityScore + 0.2 * timeScore) * 100);
};

/**
 * Score /100 d'une ligne de niche (mêmes pondérations que le score
 * d'analyse, avec un plafond de vélocité adapté au niveau niche : 10 ventes/j).
 */
export const computeNicheRowScore = (niche: {
  soldPct?: number | null;
  soldPerDay?: number | null;
  timeToSellHours?: number | null;
}): number | null => {
  const { soldPct, soldPerDay, timeToSellHours } = niche;
  if (soldPct == null && soldPerDay == null && timeToSellHours == null) return null;

  const pctScore = Math.min((soldPct ?? 0) / 30, 1);
  const velocityScore = Math.min((soldPerDay ?? 0) / 10, 1);
  const timeScore = 1 - Math.min((timeToSellHours ?? 14 * 24) / (14 * 24), 1);

  return Math.round((0.5 * pctScore + 0.3 * velocityScore + 0.2 * timeScore) * 100);
};

export type NicheScoreTier = 'poor' | 'average' | 'good';

export const getNicheScoreTier = (score: number | null): NicheScoreTier => {
  if (score == null || score < 35) return 'poor';
  if (score < 65) return 'average';
  return 'good';
};

/** CA mensuel estimé = ventes/jour × prix moyen × 30,4 jours. */
export const estimateMonthlyRevenue = (stats: AnalysisStatisticsData | undefined): number | null => {
  if (!stats || stats.salesPerDay == null || stats.averageSalePrice == null) return null;
  return stats.salesPerDay * stats.averageSalePrice * 30.4;
};

/** Format compact d'un CA : "9 630 €" / "1,2 M€". */
export const formatRevenue = (amount: number | null, lang = 'fr'): string => {
  if (amount == null) return '—';
  const locale = lang.startsWith('en') ? 'en-GB' : 'fr-FR';
  if (amount >= 1_000_000) {
    return `${(amount / 1_000_000).toLocaleString(locale, { maximumFractionDigits: 1 })} M€`;
  }
  return `${Math.round(amount).toLocaleString(locale)} €`;
};
